import Button from "components/common/controls/Button";
import InputField, {
  InputFeildProps,
} from "components/common/controls/InputField";
import PromineoPhoneInput, {
  PhoneInputValueChangedEventArg,
  PromineoPhoneInputProps,
} from "components/common/controls/PromineoPhoneInput";
import { useEffect, useState } from "react";
import { SignInSignUpOption } from "shared/enums/SignInSignUpOptionEnum";
import PhoneVerificationCodeRequest from "interfaces/request/PhoneVerificationCodeRequest";
import EmailVerificationCodeRequest from "interfaces/request/EmailVerificationCodeRequest";
import VerificationResponse from "interfaces/response/VerificationResponse";
import VerificationCodeProviderPopup from "./VerificationCodeProviderPopup";
import {
  requestForEmailVerificationCodeAsync,
  requestForPhoneVerificationCodeAsync,
  requestToVerifyVerificationCodeAsync,
} from "apis/VerificationCodeApi";
import VerifyCodeRequest from "interfaces/request/VerifyCodeRequest";
import LinkButton from "components/common/LinkButton";
import { GetNewId, isValidEmail } from "shared/utility/CommonUtility";
import ControlInformationDisplay from "components/common/ControlInformationDisplay";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useDispatch } from "react-redux";
import {
  requestToUpdateCurrentUserEmailAsync,
  requestToUpdateCurrentUserPhoneAsync,
} from "store/slices/UserSlice";
import UserPhoneUpdateRequest from "interfaces/request/UserPhoneUpdateRequest";
import UserEmailUpdateRequest from "interfaces/request/UserEmailUpdateRequest";
import { AppDispatch } from "store/store";
import Loader from "components/common/controls/Loader";

interface Props {
  emailInputProps?: InputFeildProps;
  phoneInputProps?: PromineoPhoneInputProps;
  verificationType: SignInSignUpOption;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
}

export default function UserIdentifierWithVerifcation(props: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const [emailInputKey, setEmailInputKey] = useState(GetNewId());
  const [phoneInputKey, setPhoneInputKey] = useState(GetNewId());
  const [unformattedPhoneNumber, setUnformattedPhoneNumber] = useState(
    props.phoneInputProps?.value || ""
  );
  const [
    isVerificationCodeProviderVisible,
    setIsVerificationCodeProviderVisible,
  ] = useState(false);
  const [verificationResponse, setVerificationResponse] =
    useState<VerificationResponse>({
      receiverAddress: "",
      expiryTime: "",
      token: "",
      code: "",
    });

  const [verificationRequest, setVerificationRequest] = useState<{
    email?: string;
    phone?: string;
    phoneCode?: string;
    phoneWithPhoneCode?: string;
  }>();

  const [isLoading, setIsLoading] = useState(false);

  const {
    verificationType,
    emailInputProps,
    phoneInputProps,
    isEmailVerified,
    isPhoneVerified,
  } = props;

  useEffect(() => {
    if (emailInputProps?.defaultValue) {
      setVerificationRequest({
        email: emailInputProps?.defaultValue,
      });
    }

    if (phoneInputProps?.value) {
      setVerificationRequest({
        phoneWithPhoneCode: phoneInputProps?.value,
      });
    }
  }, [emailInputProps?.defaultValue, phoneInputProps?.value]);

  const checkIfVerificationTypeIsEmail = () => {
    return verificationType === SignInSignUpOption.Email;
  };

  const checkIfVerificationTypeIsPhone = () => {
    return verificationType === SignInSignUpOption.Phone;
  };

  const checkIfEditorButtonShouldBeVisible = () => {
    return (
      (checkIfVerificationTypeIsEmail() &&
        emailInputProps?.defaultValue !== verificationRequest?.email) ||
      (checkIfVerificationTypeIsPhone() &&
        phoneInputProps?.value !== unformattedPhoneNumber)
    );
  };

  const handleUnformattedPhoneValueChanged = (phone: string) => {
    setUnformattedPhoneNumber(phone);
  };

  const handlePhoneNumberChanged = (arg: PhoneInputValueChangedEventArg) => {
    setVerificationRequest({
      phoneCode: arg.countryCode,
      phone: arg.nationalNumber,
      phoneWithPhoneCode: arg.number,
    });
  };

  const handleEmailChanged = ({ event }: any) => {
    const value = event.target.value;
    setVerificationRequest({
      email: value,
    });
  };

  const handleVerifyButtonClick = () => {
    if (!checkIfInformationIsValid()) {
      return;
    }

    const handleVerificationCodeSuccessResponse = (
      response: VerificationResponse
    ) => {
      setIsVerificationCodeProviderVisible(true);
      setVerificationResponse(response);
    };

    const emailCodeRequest: EmailVerificationCodeRequest = {};
    const phoneCodeRequest: PhoneVerificationCodeRequest = {};

    if (checkIfVerificationTypeIsEmail()) {
      emailCodeRequest.email = verificationRequest?.email;

      setIsLoading(true);
      requestForEmailVerificationCodeAsync(emailCodeRequest)
        .then(handleVerificationCodeSuccessResponse)
        .finally(() => {
          setIsLoading(false);
        });
    }

    if (checkIfVerificationTypeIsPhone()) {
      phoneCodeRequest.phone = verificationRequest?.phoneWithPhoneCode;

      setIsLoading(true);
      requestForPhoneVerificationCodeAsync(phoneCodeRequest)
        .then(handleVerificationCodeSuccessResponse)
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const requestToVerifyVerificationCode = (code: string) => {
    const verifyCodeRequest: VerifyCodeRequest = {
      code: code,
      token: verificationResponse?.token,
      type: verificationType,
      identifier: verificationResponse.receiverAddress,
    };

    setIsLoading(true);
    requestToVerifyVerificationCodeAsync(verifyCodeRequest)
      .then(() => {
        if (checkIfVerificationTypeIsEmail()) {
          updateUserEmail(code);
        } else {
          updateUserPhone(code);
        }
      })
      .then(() => {
        closeVerificationProvider();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const updateUserEmail = (verificationCode: string) => {
    const emailUpdateRequest: UserEmailUpdateRequest = {
      code: verificationCode,
      token: verificationResponse.token,
      email: verificationResponse.receiverAddress || "",
    };

    dispatch(requestToUpdateCurrentUserEmailAsync(emailUpdateRequest)).finally(
      () => {
        setIsLoading(false);
      }
    );
  };

  const updateUserPhone = (verificationCode: string) => {
    const phoneUpdateRequest: UserPhoneUpdateRequest = {
      code: verificationCode,
      token: verificationResponse?.token,
      phone: verificationRequest?.phone || "",
      phoneCode: verificationRequest?.phoneCode || "",
    };

    dispatch(requestToUpdateCurrentUserPhoneAsync(phoneUpdateRequest)).finally(
      () => {
        setIsLoading(false);
      }
    );
  };

  const closeVerificationProvider = () => {
    setIsVerificationCodeProviderVisible(false);
  };

  const cancelEditing = () => {
    setEmailInputKey(GetNewId());
    setPhoneInputKey(GetNewId());
    setUnformattedPhoneNumber(phoneInputProps?.value || "");
    setVerificationRequest({
      email: emailInputProps?.defaultValue,
      phoneWithPhoneCode: phoneInputProps?.value || "",
    });
  };

  const getVerificationStatusMessage = () => {
    let message = "";

    if (checkIfVerificationTypeIsEmail()) {
      message = checkIfIdentifierIsVerified()
        ? "Email is verified"
        : "Email is not verified";
    }

    if (checkIfVerificationTypeIsPhone()) {
      message = checkIfIdentifierIsVerified()
        ? "Phone is verified"
        : "Phone is not verified";
    }

    return message;
  };

  const checkIfIdentifierIsVerified = () => {
    return (
      (checkIfVerificationTypeIsEmail() && isEmailVerified) ||
      (checkIfVerificationTypeIsPhone() && isPhoneVerified)
    );
  };

  const checkIfInformationIsValid = () => {
    if (checkIfVerificationTypeIsEmail()) {
      return isValidEmail(verificationRequest?.email || "");
    }

    if (checkIfVerificationTypeIsPhone()) {
      return isValidPhoneNumber(verificationRequest?.phoneWithPhoneCode);
    }

    return false;
  };

  return (
    <>
      <div
        className={`${
          checkIfEditorButtonShouldBeVisible()
            ? "bg-promGray3 rounded-lg pb-2"
            : ""
        }`}
      >
        <div>
          {verificationType === SignInSignUpOption.Email ? (
            <InputField
              mode="email"
              label="Email"
              placeholder="e.g. john.doe@provider.com"
              {...emailInputProps}
              onChange={handleEmailChanged}
              key={emailInputKey}
            />
          ) : (
            <PromineoPhoneInput
              {...phoneInputProps}
              value={verificationRequest?.phoneWithPhoneCode || ""}
              onPhoneNumberChanged={handlePhoneNumberChanged}
              onChange={handleUnformattedPhoneValueChanged}
              immediateValidation
              key={phoneInputKey}
            />
          )}
        </div>
        {checkIfEditorButtonShouldBeVisible() ? (
          <div className="flex mt-2">
            <div className="w-full px-2">
              <div className="float-left mt-4">
                <LinkButton className="text-primaryRed" onClick={cancelEditing}>
                  Cancel
                </LinkButton>
              </div>
              <div className="float-right">
                <Button text="VERIFY" onClick={handleVerifyButtonClick} />
              </div>
            </div>
          </div>
        ) : (
          <ControlInformationDisplay
            variant={checkIfIdentifierIsVerified() ? "success" : "error"}
            message={getVerificationStatusMessage()}
          />
        )}
      </div>
      {isVerificationCodeProviderVisible && (
        <VerificationCodeProviderPopup
          onClose={closeVerificationProvider}
          verificationCodeProviderProps={{
            authenticationType: verificationType,
            codeSendingAddress: verificationResponse.receiverAddress,
            expireTime: verificationResponse.expiryTime,
            onVerificationCodeInputComplete: requestToVerifyVerificationCode,
            token: verificationResponse.token,
            backLinkText: "Cancel",
            onBackLinkClick: closeVerificationProvider,
          }}
        />
      )}

      <Loader isVisible={isLoading} />
    </>
  );
}
